/**
 * @file custom-health-directory.js
 * @description Logic for the Health Directory Dynamic Table MVP.
 */

const state = {
  rawData: [],
  filteredData: [],
  vzlaData: [],
  currentPage: 1,
  rowsPerPage: 10,
  currentSearchPhrase: '',
  currentFilterEstado: '',
  currentFilterLocalidad: '',
  currentFilterEspecialidad: '',
  currentFilterSubEspecialidad: '',
  activeMobileTab: 'estado',
  cachedStates: new Set(), 
  cachedLocalities: new Set(),
  cachedEspecialidades: new Set(),
  cachedSubEspecialidades: new Set(),
  categoryConfig: {
    showCustomFilter: false,
    customFilterLabel: '',
    customFilterValue: ''
  },
  activeColumns: [],
  currentCategoryTitle: '',
  pdfLogoData: null
};

const domNodes = {
  sectionElement: null,
  categoriesCarouselContainer: null,
  categoriesGrid: null,
  categoriesScrollWrapper: null,
  arrowLeft: null,
  arrowRight: null,
  searchInput: null,
  filterEstado: null,
  filterLocalidad: null,
  filterEspecialidad: null,
  filterSubEspecialidad: null,
  resultsContainer: null,
  rowsSelector: null,
  paginationWrapper: null,
  paginationControls: null,
  loader: null,
  toggleFiltersBtn: null,
  filtersPanel: null,
  mobileFiltersPanel: null,
  mobileTabsContainer: null,
  mobileTabEspecialidad: null,
  mobileTabSubEspecialidad: null,
  mobileFilterList: null,
  activeFiltersWrapper: null,
  downloadCard: null,
  downloadTitle: null,
  downloadBtnText: null,
  downloadDesc: null,
  downloadBtn: null
};

document.addEventListener('DOMContentLoaded', () => {
  initHealthDirectory();
});

/**
 * Loads the Venezuela states and cities JSON database
 */
async function loadVzlaData() {
  if (state.vzlaData && state.vzlaData.length > 0) return;
  const section = domNodes.sectionElement || document.querySelector('.health-directory-section');
  if (!section) return;
  const fileUrl = section.getAttribute('data-vzla-json');
  if (!fileUrl) return;

  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`Failed to load Venezuela JSON: ${response.status}`);
    }
    state.vzlaData = await response.json();
  } catch (e) {
    console.error('Error loading Venezuela states/cities JSON:', e);
  }
}

/**
 * Normalizes strings for robust fuzzy comparison (removes accents, punctuation, file extensions)
 */
function normalizeCategoryStr(str) {
  if (!str) return '';
  return str
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\.json$/i, '')
    .replace(/[^a-z0-9]/g, '')
    .trim();
}

/**
 * Finds the target category button based on URL parameter with multi-strategy matching
 */
function findTargetCategoryButton(param, allBtns, allGroupBtns) {
  if (!param || !allBtns || allBtns.length === 0) return null;
  const normParam = normalizeCategoryStr(param);
  if (!normParam) return null;

  const btnList = Array.from(allBtns);

  // 1. Direct match on data-id, title or aria-label
  for (const btn of btnList) {
    const id = normalizeCategoryStr(btn.getAttribute('data-id'));
    const title = normalizeCategoryStr(btn.querySelector('.hd-cat-title')?.textContent);
    const aria = normalizeCategoryStr(btn.getAttribute('aria-label'));
    if (normParam === id || normParam === title || normParam === aria) {
      return btn;
    }
  }

  // 2. Known aliases and common typos
  const aliases = {
    'cliicas': 'clinicas',
    'clinica': 'clinicas',
    'tallerdeauto': 'taller',
    'talleres': 'taller',
    'atencionprimaria': 'amp',
    'atencionmedicaprimaria': 'amp',
    'atencionmedica': 'amp',
    'casacomercial': 'casacomercial',
    'casascomerciales': 'casacomercial',
    'farmacias': 'farmacia',
    'ambulancias': 'ambulancia'
  };

  const targetAlias = aliases[normParam];
  if (targetAlias) {
    for (const btn of btnList) {
      const id = normalizeCategoryStr(btn.getAttribute('data-id'));
      const title = normalizeCategoryStr(btn.querySelector('.hd-cat-title')?.textContent);
      if (id === targetAlias || title === targetAlias) {
        return btn;
      }
    }
  }

  // 3. Singular / Plural root comparison
  const stem = normParam.replace(/(es|s)$/i, '');
  for (const btn of btnList) {
    const id = normalizeCategoryStr(btn.getAttribute('data-id'));
    const title = normalizeCategoryStr(btn.querySelector('.hd-cat-title')?.textContent);
    const idStem = id.replace(/(es|s)$/i, '');
    const titleStem = title.replace(/(es|s)$/i, '');
    if (stem && (stem === idStem || stem === titleStem)) {
      return btn;
    }
    if (id && (id.startsWith(normParam) || normParam.startsWith(id))) {
      return btn;
    }
    if (title && (title.startsWith(normParam) || normParam.startsWith(title))) {
      return btn;
    }
  }

  // 4. Target JSON filename matching
  for (const btn of btnList) {
    const target = normalizeCategoryStr(btn.getAttribute('data-target'));
    if (target && (target.includes(normParam) || (stem && target.includes(stem)))) {
      return btn;
    }
  }

  // 5. Parent Group match (e.g. ?cat=auto or ?cat=salud)
  if (allGroupBtns && allGroupBtns.length > 0) {
    for (const gBtn of Array.from(allGroupBtns)) {
      const gName = normalizeCategoryStr(gBtn.getAttribute('data-group-name'));
      if (gName && (normParam === gName || gName.startsWith(normParam) || normParam.startsWith(gName))) {
        const firstInGroup = btnList.find(b => normalizeCategoryStr(b.getAttribute('data-parent-group')) === gName);
        if (firstInGroup) return firstInGroup;
      }
    }
  }

  return null;
}

/**
 * Entry point of the Directory
 */
async function initHealthDirectory() {
  cacheDomNodes();
  if (!domNodes.categoriesGrid) return; // Early return if section is not present

  preloadPdfLogo();
  await loadVzlaData();
  bindEvents();

  // 1. Check for category parameter in URL
  const urlParams = new URLSearchParams(window.location.search);
  const catParam = urlParams.get('cat');

  const allBtns = domNodes.categoriesGrid.querySelectorAll('.hd-category-btn');
  const allGroupBtns = document.querySelectorAll('.hd-group-btn');
  let targetBtn = null;

  if (catParam) {
    targetBtn = findTargetCategoryButton(catParam, allBtns, allGroupBtns);
  }

  // 2. Logic for selecting the initial category
  if (!targetBtn) {
    // Fallback: Use the one with 'active' class (set by Liquid) or the first one available
    targetBtn = domNodes.categoriesGrid.querySelector('.hd-category-btn.active') || allBtns[0];
  }

  if (!targetBtn) return;

  // Sincronizar grupo padre con el botón de la categoría activa
  const parentGroupName = targetBtn.getAttribute('data-parent-group');

  if (parentGroupName && allGroupBtns.length > 0) {
    // A. Activar el botón de grupo padre correspondiente
    allGroupBtns.forEach(gBtn => {
      if (gBtn.getAttribute('data-group-name') === parentGroupName) {
        gBtn.classList.add('active');
      } else {
        gBtn.classList.remove('active');
      }
    });

    // B. Filtrar categorías del segundo nivel
    allBtns.forEach(btn => {
      const btnGroup = btn.getAttribute('data-parent-group');
      if (btnGroup === parentGroupName) {
        btn.classList.remove('hd-hidden');
      } else {
        btn.classList.add('hd-hidden');
      }
    });
  } else if (allGroupBtns.length > 0) {
    // Si no hay categoría por parámetro de URL, activamos la primera de forma predeterminada
    const firstGroupBtn = allGroupBtns[0];
    const firstGroupName = firstGroupBtn.getAttribute('data-group-name');

    allGroupBtns.forEach(b => b.classList.remove('active'));
    firstGroupBtn.classList.add('active');

    allBtns.forEach(btn => {
      const btnGroup = btn.getAttribute('data-parent-group');
      if (btnGroup === firstGroupName) {
        btn.classList.remove('hd-hidden');
      } else {
        btn.classList.add('hd-hidden');
      }
    });

    // Asegurar que la categoría activa sea la primera del grupo padre activo
    const visibleBtns = Array.from(allBtns).filter(b => !b.classList.contains('hd-hidden'));
    if (visibleBtns.length > 0) {
      targetBtn = visibleBtns[0];
    }
  }

  // Ensure we start clean and activate the target
  allBtns.forEach(b => b.classList.remove('active'));
  handleCategorySwitch({ currentTarget: targetBtn }, allBtns);

  // Auto scroll to active tab if it's out of view and scroll into section
  setTimeout(() => {
    scrollActiveCategoryIntoView(targetBtn);
  }, 150);
}

/**
 * Selects and caches DOM nodes
 */
function cacheDomNodes() {
  domNodes.sectionElement = document.querySelector('.health-directory-section');
  domNodes.categoriesCarouselContainer = document.querySelector('.hd-categories-carousel-container');
  domNodes.categoriesGrid = document.getElementById('hdCategoriesGrid');
  domNodes.categoriesScrollWrapper = document.getElementById('hdCategoriesScrollWrapper');
  domNodes.arrowLeft = document.getElementById('hdArrowLeft');
  domNodes.arrowRight = document.getElementById('hdArrowRight');
  domNodes.searchInput = document.getElementById('hdGlobalSearch');
  domNodes.filterEstado = document.getElementById('hdFilterEstado');
  domNodes.filterLocalidad = document.getElementById('hdFilterLocalidad');
  domNodes.filterEspecialidad = document.getElementById('hdFilterEspecialidad');
  domNodes.filterSubEspecialidad = document.getElementById('hdFilterSubEspecialidad');
  domNodes.resultsContainer = document.getElementById('hdResultsContainer');
  domNodes.rowsSelector = document.getElementById('hdRowsPerPage');
  domNodes.paginationWrapper = document.getElementById('hdPaginationWrapper');
  domNodes.paginationControls = document.getElementById('hdPaginationControls');
  domNodes.loader = document.getElementById('hdLoader');
  domNodes.toggleFiltersBtn = document.getElementById('hdToggleFiltersBtn');
  domNodes.filtersPanel = document.getElementById('hdFiltersPanel');
  domNodes.mobileFiltersPanel = document.getElementById('hdMobileFiltersPanel');
  domNodes.mobileTabsContainer = document.getElementById('hdMobileTabsContainer');
  domNodes.mobileTabEspecialidad = document.getElementById('hdMobileTabEspecialidad');
  domNodes.mobileTabSubEspecialidad = document.getElementById('hdMobileTabSubEspecialidad');
  domNodes.mobileFilterList = document.getElementById('hdMobileFilterList');
  domNodes.activeFiltersWrapper = document.getElementById('hdActiveFiltersWrapper');
  domNodes.downloadCard = document.getElementById('hdDownloadCard');
  domNodes.downloadTitle = document.getElementById('hdDownloadTitle');
  domNodes.downloadBtnText = document.getElementById('hdDownloadBtnText');
  domNodes.downloadDesc = document.getElementById('hdDownloadDesc');
  domNodes.downloadBtn = document.getElementById('hdDownloadBtn');
  domNodes.categoryNotice = document.getElementById('hdCategoryNotice');
}

/**
 * Toggle arrows visibility based on scroll position
 */
function toggleArrowsVisually() {
  if (!domNodes.categoriesScrollWrapper || !domNodes.arrowLeft || !domNodes.arrowRight) return;
  const { scrollWidth, clientWidth, scrollLeft } = domNodes.categoriesScrollWrapper;

  if (scrollWidth <= clientWidth + 5) {
    domNodes.arrowLeft.classList.add('hd-hidden');
    domNodes.arrowRight.classList.add('hd-hidden');
  } else {
    if (scrollLeft <= 5) domNodes.arrowLeft.classList.add('hd-hidden');
    else domNodes.arrowLeft.classList.remove('hd-hidden');

    if (scrollLeft + clientWidth >= scrollWidth - 5) domNodes.arrowRight.classList.add('hd-hidden');
    else domNodes.arrowRight.classList.remove('hd-hidden');
  }
}

/**
 * Bind DOM events
 */
function bindEvents() {
  if (domNodes.arrowLeft && domNodes.categoriesScrollWrapper) {
    domNodes.arrowLeft.addEventListener('click', () => {
      const cardWidth = domNodes.categoriesGrid.firstElementChild ? domNodes.categoriesGrid.firstElementChild.offsetWidth : 200;
      domNodes.categoriesScrollWrapper.scrollBy({ left: -(cardWidth + 15), behavior: 'smooth' });
    });
  }

  if (domNodes.arrowRight && domNodes.categoriesScrollWrapper) {
    domNodes.arrowRight.addEventListener('click', () => {
      const cardWidth = domNodes.categoriesGrid.firstElementChild ? domNodes.categoriesGrid.firstElementChild.offsetWidth : 200;
      domNodes.categoriesScrollWrapper.scrollBy({ left: (cardWidth + 15), behavior: 'smooth' });
    });
  }

  if (domNodes.categoriesScrollWrapper) {
    domNodes.categoriesScrollWrapper.addEventListener('scroll', toggleArrowsVisually);
    window.addEventListener('resize', toggleArrowsVisually);
    setTimeout(toggleArrowsVisually, 100);
  }

  if (domNodes.downloadBtn) {
    domNodes.downloadBtn.addEventListener('click', generatePDF);
  }

  // Category switch delegations click
  const categoryBtns = domNodes.categoriesGrid.querySelectorAll('.hd-category-btn');
  categoryBtns.forEach(btn => {
    btn.addEventListener('click', (e) => handleCategorySwitch(e, categoryBtns));
  });

  // Group switch click (Nivel 1)
  const allGroupBtns = document.querySelectorAll('.hd-group-btn');
  allGroupBtns.forEach(btn => {
    btn.addEventListener('click', handleGroupSwitch);
  });

  // Search input typing (Debounced lightly or instant)
  domNodes.searchInput.addEventListener('input', (e) => {
    state.currentSearchPhrase = e.target.value;
    state.currentPage = 1;
    applyFilters();
  });

  // Select Filters change
  domNodes.filterEstado.addEventListener('change', (e) => {
    state.currentFilterEstado = e.target.value;
    state.currentFilterLocalidad = '';
    if (domNodes.filterLocalidad) domNodes.filterLocalidad.value = '';
    state.currentPage = 1;
    populateSelectFilters();
    applyFilters();
  });

  domNodes.filterLocalidad.addEventListener('change', (e) => {
    state.currentFilterLocalidad = e.target.value;
    state.currentPage = 1;
    applyFilters();
  });

  domNodes.filterEspecialidad.addEventListener('change', (e) => {
    state.currentFilterEspecialidad = e.target.value;
    checkSubEspecialidadVisibility();
    state.currentPage = 1;
    populateSelectFilters();
    applyFilters();
  });

  if (domNodes.filterSubEspecialidad) {
    domNodes.filterSubEspecialidad.addEventListener('change', (e) => {
      state.currentFilterSubEspecialidad = e.target.value;
      state.currentPage = 1;
      applyFilters();
    });
  }

  // Pagination Rows Per Page change
  domNodes.rowsSelector.addEventListener('change', (e) => {
    state.rowsPerPage = parseInt(e.target.value, 10);
    state.currentPage = 1;
    renderCurrentPage();
  });

  // Toggle Filters Mobile
  if (domNodes.toggleFiltersBtn) {
    domNodes.toggleFiltersBtn.addEventListener('click', () => {
      domNodes.mobileFiltersPanel.classList.toggle('hd-panel-expanded');
      renderMobileFilterList();
    });
  }

  // Mobile Tabs Clicks
  if (domNodes.mobileTabsContainer) {
    domNodes.mobileTabsContainer.addEventListener('click', (e) => {
      const tabBtn = e.target.closest('.hd-mf-tab');
      if (!tabBtn) return;

      // Update active tab UI
      domNodes.mobileTabsContainer.querySelectorAll('.hd-mf-tab').forEach(b => b.classList.remove('active'));
      tabBtn.classList.add('active');

      state.activeMobileTab = tabBtn.getAttribute('data-tab');
      renderMobileFilterList();
    });
  }

  // Mobile Filter List Item Click
  if (domNodes.mobileFilterList) {
    domNodes.mobileFilterList.addEventListener('click', (e) => {
      const itemBtn = e.target.closest('.hd-mf-item');
      if (!itemBtn) return;

      const selectedValue = itemBtn.getAttribute('data-val');

      // Update internal state depending on active tab
      if (state.activeMobileTab === 'estado') {
        state.currentFilterEstado = selectedValue;
        domNodes.filterEstado.value = selectedValue;
        state.currentFilterLocalidad = '';
        domNodes.filterLocalidad.value = '';
      } else if (state.activeMobileTab === 'especialidad') {
        state.currentFilterEspecialidad = selectedValue;
        domNodes.filterEspecialidad.value = selectedValue;
        checkSubEspecialidadVisibility();
      } else if (state.activeMobileTab === 'subespecialidad') {
        state.currentFilterSubEspecialidad = selectedValue;
        if (domNodes.filterSubEspecialidad) domNodes.filterSubEspecialidad.value = selectedValue;
      } else {
        state.currentFilterLocalidad = selectedValue;
        domNodes.filterLocalidad.value = selectedValue;
      }

      state.currentPage = 1;
      populateSelectFilters();
      applyFilters();

      // Close panel for immediate feedback
      domNodes.mobileFiltersPanel.classList.remove('hd-panel-expanded');
    });
  }

  // Active Chips click (clears individual active filters)
  if (domNodes.activeFiltersWrapper) {
    domNodes.activeFiltersWrapper.addEventListener('click', (e) => {
      const chip = e.target.closest('.hd-filter-chip');
      if (!chip) return;
      const filterToClear = chip.getAttribute('data-clear');
      if (filterToClear === 'estado') {
        state.currentFilterEstado = '';
        if (domNodes.filterEstado) domNodes.filterEstado.value = '';
        state.currentFilterLocalidad = '';
        if (domNodes.filterLocalidad) domNodes.filterLocalidad.value = '';
      } else if (filterToClear === 'localidad') {
        state.currentFilterLocalidad = '';
        if (domNodes.filterLocalidad) domNodes.filterLocalidad.value = '';
      } else if (filterToClear === 'especialidad') {
        state.currentFilterEspecialidad = '';
        if (domNodes.filterEspecialidad) domNodes.filterEspecialidad.value = '';
        checkSubEspecialidadVisibility();
      } else if (filterToClear === 'subespecialidad') {
        state.currentFilterSubEspecialidad = '';
        if (domNodes.filterSubEspecialidad) domNodes.filterSubEspecialidad.value = '';
      }
      state.currentPage = 1;
      populateSelectFilters();
      applyFilters();
    });
  }
  domNodes.resultsContainer.addEventListener('click', (e) => {
    const toggleBtn = e.target.closest('.hd-row-toggle');
    if (!toggleBtn) return;

    const row = toggleBtn.closest('.hd-table-row');
    if (!row) return;

    const bodyRow = row.querySelector('.hd-row-body');
    if (!bodyRow) return;

    const isExpanded = bodyRow.classList.contains('hd-expanded');

    if (isExpanded) {
      bodyRow.classList.remove('hd-expanded');
    } else {
      bodyRow.classList.add('hd-expanded');
    }
  });
}

/**
 * Handles toggling active classes and triggering JSON load
 */
function handleCategorySwitch(e, allBtns) {
  const clickedBtn = e.currentTarget;
  if (clickedBtn.classList.contains('active')) return;

  allBtns.forEach(b => b.classList.remove('active'));
  clickedBtn.classList.add('active');

  const catTitle = clickedBtn.querySelector('.hd-cat-title').textContent.trim();
  state.currentCategoryTitle = catTitle;

  if (domNodes.downloadCard) {
    domNodes.downloadCard.style.display = 'block';
    if (domNodes.downloadTitle) domNodes.downloadTitle.textContent = `Listado de ${catTitle}`;
    if (domNodes.downloadBtnText) domNodes.downloadBtnText.textContent = `Descarga el Listado en formato PDF`;
    if (domNodes.downloadDesc) domNodes.downloadDesc.textContent = `Guarda el listado completo de ${catTitle} en formato PDF. Podrás consultarlo al instante desde tu teléfono o computadora, sin necesidad de conexión a internet.`;
  }

  const colsAttr = clickedBtn.getAttribute('data-cols');
  state.activeColumns = colsAttr ? JSON.parse(colsAttr) : [];

  state.categoryConfig = {
    showCustomFilter: clickedBtn.getAttribute('data-custom-filter-show') === 'true',
    customFilterLabel: clickedBtn.getAttribute('data-custom-filter-label') || 'Filtro',
    customFilterValue: clickedBtn.getAttribute('data-custom-filter-value') || ''
  };

  // Reset SubEspecialidad filter upon category switch
  state.currentFilterSubEspecialidad = '';
  if (domNodes.filterSubEspecialidad) {
    domNodes.filterSubEspecialidad.value = '';
    domNodes.filterSubEspecialidad.classList.add('hd-hidden');
  }
  if (domNodes.mobileTabSubEspecialidad) {
    domNodes.mobileTabSubEspecialidad.classList.add('hd-hidden');
  }

  if (!state.categoryConfig.showCustomFilter || !state.categoryConfig.customFilterValue) {
    if (domNodes.filterEspecialidad) domNodes.filterEspecialidad.classList.add('hd-hidden');
    if (domNodes.mobileTabEspecialidad) domNodes.mobileTabEspecialidad.classList.add('hd-hidden');
  } else {
    if (domNodes.filterEspecialidad) {
      domNodes.filterEspecialidad.classList.remove('hd-hidden');
      domNodes.filterEspecialidad.options[0].textContent = state.categoryConfig.customFilterLabel;
    }
    if (domNodes.mobileTabEspecialidad) {
      domNodes.mobileTabEspecialidad.classList.remove('hd-hidden');
      domNodes.mobileTabEspecialidad.textContent = state.categoryConfig.customFilterLabel.toUpperCase();
    }
  }

  const noticeId = clickedBtn.getAttribute('data-notice-id');
  const hiddenNotice = noticeId ? document.getElementById(noticeId) : null;

  if (domNodes.categoryNotice) {
    if (hiddenNotice && hiddenNotice.innerHTML.trim() !== '') {
      domNodes.categoryNotice.innerHTML = hiddenNotice.innerHTML;
      domNodes.categoryNotice.style.display = 'block';
    } else {
      domNodes.categoryNotice.innerHTML = '';
      domNodes.categoryNotice.style.display = 'none';
    }
  }

  const targetJson = clickedBtn.getAttribute('data-target');
  loadCategoryData(targetJson);
}

/**
 * Checks if the SubEspecialidad filter should be visible based on current custom filter selection
 */
function checkSubEspecialidadVisibility() {
  const customField = state.categoryConfig.customFilterValue;
  const isConsulta = customField === 'tipo_de_servicio' && normalizeString(state.currentFilterEspecialidad) === 'consulta';

  if (isConsulta) {
    if (domNodes.filterSubEspecialidad) {
      domNodes.filterSubEspecialidad.classList.remove('hd-hidden');
    }
    if (domNodes.mobileTabSubEspecialidad) {
      domNodes.mobileTabSubEspecialidad.classList.remove('hd-hidden');
    }
  } else {
    state.currentFilterSubEspecialidad = '';
    if (domNodes.filterSubEspecialidad) {
      domNodes.filterSubEspecialidad.value = '';
      domNodes.filterSubEspecialidad.classList.add('hd-hidden');
    }
    if (domNodes.mobileTabSubEspecialidad) {
      domNodes.mobileTabSubEspecialidad.classList.add('hd-hidden');
      if (state.activeMobileTab === 'subespecialidad') {
        state.activeMobileTab = 'estado';
        if (domNodes.mobileTabsContainer) {
          domNodes.mobileTabsContainer.querySelectorAll('.hd-mf-tab').forEach(b => b.classList.remove('active'));
          const estadoTab = domNodes.mobileTabsContainer.querySelector('[data-tab="estado"]');
          if (estadoTab) estadoTab.classList.add('active');
        }
      }
    }
  }
}

/**
 * Resolves the active columns for the current category, dynamically filtering out
 * the "especialidad" column if we are in "Atencion Medica Primaria" and the
 * selected service type is not "consulta".
 */
function getActiveColumns() {
  let cols = (state.activeColumns && state.activeColumns.length > 0) ? state.activeColumns : [
    { label: "Proveedor de Salud", value: "nombre" },
    { label: "Dirección", value: "direccion" },
    { label: "Teléfonos", value: "telefono" },
    { label: "Depósito", value: "deposito" },
    { label: "Reemb. (100%)", value: "gastos_reembolsables" },
    { label: "Gastos Razonables", value: "gastos_razonables" }
  ];

  if (state.categoryConfig.customFilterValue === 'tipo_de_servicio') {
    const isConsulta = normalizeString(state.currentFilterEspecialidad) === 'consulta';
    if (!isConsulta) {
      cols = cols.filter(col => col.value !== 'especialidad');
    }
  }
  return cols;
}

/**
 * Switches the active parent group and filters subcategories
 */
function handleGroupSwitch(e) {
  const clickedGroupBtn = e.currentTarget;
  if (clickedGroupBtn.classList.contains('active')) return;

  const allGroupBtns = document.querySelectorAll('.hd-group-btn');
  allGroupBtns.forEach(b => b.classList.remove('active'));
  clickedGroupBtn.classList.add('active');

  const groupName = clickedGroupBtn.getAttribute('data-group-name');
  const allCategoryBtns = domNodes.categoriesGrid.querySelectorAll('.hd-category-btn');

  let firstVisibleBtn = null;

  allCategoryBtns.forEach(btn => {
    const btnGroup = btn.getAttribute('data-parent-group');
    if (btnGroup === groupName) {
      btn.classList.remove('hd-hidden');
      if (!firstVisibleBtn) firstVisibleBtn = btn;
    } else {
      btn.classList.add('hd-hidden');
      btn.classList.remove('active');
    }
  });

  if (firstVisibleBtn) {
    firstVisibleBtn.click();
  }
}

/**
 * Fetches JSON from target URL async
 */
async function loadCategoryData(fileUrl) {
  if (!fileUrl) return;

  domNodes.loader.style.display = 'block';
  domNodes.resultsContainer.innerHTML = '';
  domNodes.paginationWrapper.style.display = 'none';

  try {
    // 1. Separar las URLs por coma
    const urls = fileUrl.split(',').map(url => url.trim());

    // 2. Realizar las peticiones fetch de forma paralela
    const fetchPromises = urls.map(async (url) => {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Network response was not ok for ${url}: ${response.status}`);
      }
      return response.json();
    });

    // 3. Esperar que se completen todas las descargas
    const datasets = await Promise.all(fetchPromises);

    // 4. Unificar (Merge) todos los conjuntos de datos en un solo array
    const combinedData = datasets.flat();

    // 5. Enviar al procesador exitoso de datos
    handleDataSuccess(combinedData);

  } catch (error) {
    console.error('Error fetching category data:', error);
    domNodes.loader.style.display = 'none';
    domNodes.resultsContainer.innerHTML = `<div class="hd-error-msg">No se pudieron cargar los datos para esta categoría. Verifique la subida del JSON.</div>`;
  }
}

/**
 * Processes successful JSON data
 */
function handleDataSuccess(data) {
  // Preprocess rawData using venezuela.json for official states and cities
  let processedData = data || [];
  if (state.vzlaData && state.vzlaData.length > 0) {
    processedData = processedData.map(item => {
      let officialEstado = item.estado || '';
      let officialLocalidad = item.localidad || '';

      const normEstado = normalizeString(item.estado);
      const matchedState = state.vzlaData.find(v => normalizeString(v.estado) === normEstado);
      if (matchedState) {
        officialEstado = matchedState.estado;

        if (matchedState.ciudades && Array.isArray(matchedState.ciudades)) {
          const normLocalidad = normalizeString(item.localidad);
          const matchedCity = matchedState.ciudades.find(c => normalizeString(c) === normLocalidad);
          if (matchedCity) {
            officialLocalidad = matchedCity;
          }
        }
      }

      return {
        ...item,
        estado: officialEstado,
        localidad: officialLocalidad
      };
    });
  }

  // Multi-criteria sort: Estado (A-Z) → Localidad (A-Z) → indexOrder (>0) vs sin indexOrder → Tipo de Negociación → Nombre (A-Z)
  processedData.sort(compareAllies);

  state.rawData = processedData;
  state.currentPage = 1;
  state.currentSearchPhrase = '';
  state.currentFilterEstado = '';
  state.currentFilterLocalidad = '';
  state.currentFilterEspecialidad = '';

  if (domNodes.searchInput) domNodes.searchInput.value = '';
  if (domNodes.filterEstado) domNodes.filterEstado.value = '';
  if (domNodes.filterLocalidad) domNodes.filterLocalidad.value = '';
  if (domNodes.filterEspecialidad) domNodes.filterEspecialidad.value = '';

  populateSelectFilters();
  applyFilters();

  domNodes.loader.style.display = 'none';
  if (state.rawData.length > 0) {
    domNodes.paginationWrapper.style.display = 'flex';
  }
}

/**
 * Extracts unique States and Localities and populates selects
 */
function populateSelectFilters() {
  state.cachedStates = new Set();
  state.cachedLocalities = new Set();
  state.cachedEspecialidades = new Set();
  state.cachedSubEspecialidades = new Set();

  // 1. Gather all unique states and custom filter values from the raw data
  const customField = state.categoryConfig.customFilterValue;
  state.rawData.forEach(item => {
    if (item.estado) state.cachedStates.add(item.estado);
    if (customField && item[customField]) {
      state.cachedEspecialidades.add(item[customField]);
    }
    // Populate subspecialties if type of service is consulta
    if (item.especialidad && normalizeString(item[customField]) === 'consulta') {
      state.cachedSubEspecialidades.add(item.especialidad);
    }
  });

  // 2. Gather unique localities: if a state filter is active, only show localities of that state
  state.rawData.forEach(item => {
    if (item.localidad) {
      if (!state.currentFilterEstado || normalizeString(item.estado) === normalizeString(state.currentFilterEstado)) {
        state.cachedLocalities.add(item.localidad);
      }
    }
  });

  // 3. Build HTML options preserving selections if they are still valid
  const selectedEstado = state.currentFilterEstado;
  const selectedLocalidad = state.currentFilterLocalidad;
  const selectedEspecialidad = state.currentFilterEspecialidad;
  const selectedSubEspecialidad = state.currentFilterSubEspecialidad;

  const statesHTML = `<option value="">Estado</option>` +
    Array.from(state.cachedStates).sort().map(s => {
      const sel = (normalizeString(s) === normalizeString(selectedEstado)) ? 'selected' : '';
      return `<option value="${s}" ${sel}>${s}</option>`;
    }).join('');

  const localitiesHTML = `<option value="">Localidad</option>` +
    Array.from(state.cachedLocalities).sort().map(l => {
      const sel = (normalizeString(l) === normalizeString(selectedLocalidad)) ? 'selected' : '';
      return `<option value="${l}" ${sel}>${l}</option>`;
    }).join('');

  const customLabel = state.categoryConfig.customFilterLabel || 'Especialidad';
  const especialidadesHTML = `<option value="">${customLabel}</option>` +
    Array.from(state.cachedEspecialidades).sort().map(e => {
      const sel = (e === selectedEspecialidad) ? 'selected' : '';
      return `<option value="${e}" ${sel}>${e}</option>`;
    }).join('');

  const subEspecialidadesHTML = `<option value="">Especialidad</option>` +
    Array.from(state.cachedSubEspecialidades).sort().map(e => {
      const sel = (e === selectedSubEspecialidad) ? 'selected' : '';
      return `<option value="${e}" ${sel}>${e}</option>`;
    }).join('');

  if (domNodes.filterEstado) domNodes.filterEstado.innerHTML = statesHTML;
  if (domNodes.filterLocalidad) domNodes.filterLocalidad.innerHTML = localitiesHTML;
  if (domNodes.filterEspecialidad) domNodes.filterEspecialidad.innerHTML = especialidadesHTML;
  if (domNodes.filterSubEspecialidad) domNodes.filterSubEspecialidad.innerHTML = subEspecialidadesHTML;
}

/**
 * Renders the options for the active mobile tab
 */
function renderMobileFilterList() {
  if (!domNodes.mobileFilterList) return;
  const isEstado = state.activeMobileTab === 'estado';
  const isEsp = state.activeMobileTab === 'especialidad';
  const isSubEsp = state.activeMobileTab === 'subespecialidad';
  const targetSet = isEstado ? state.cachedStates : (isEsp ? state.cachedEspecialidades : (isSubEsp ? state.cachedSubEspecialidades : state.cachedLocalities));
  const currentValue = isEstado ? state.currentFilterEstado : (isEsp ? state.currentFilterEspecialidad : (isSubEsp ? state.currentFilterSubEspecialidad : state.currentFilterLocalidad));

  if (!targetSet) return;

  let html = `<button type="button" class="hd-mf-item ${currentValue === '' ? 'selected' : ''}" data-val="">Todos / Limpiar</button>`;

  Array.from(targetSet).sort().forEach(val => {
    const selectedClass = (val === currentValue) ? 'selected' : '';
    html += `<button type="button" class="hd-mf-item ${selectedClass}" data-val="${val}">${val}</button>`;
  });

  domNodes.mobileFilterList.innerHTML = html;
}

/**
 * Normalizes text to lowercase and removes diacritics
 */
function normalizeString(str) {
  if (!str) return '';
  return str.toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Converts text to Title Case supporting Spanish Unicode and accented characters
 */
function toTitleCase(str) {
  if (!str) return '';
  return str
    .toString()
    .toLowerCase()
    .replace(/(^|[^\p{L}\p{N}]+)(\p{L})/gu, (match, prefix, char) => prefix + char.toUpperCase());
}

/**
 * Helper to determine negotiation priority rank and normalized text
 * Rank 1: 100% Reembolsables (e.g. '100% gastos reembolsables(*)')
 * Rank 2: Gastos Razonables (e.g. 'Aplicación de gastos razonables')
 * Rank 3: Other custom negotiation text
 * Rank 4: Blank / null / empty
 */
function getNegotiationRank(tipoNeg) {
  const norm = normalizeString(tipoNeg).trim();
  if (!norm) return { rank: 4, text: '' };
  if (norm.includes('100') || norm.includes('reembols')) return { rank: 1, text: norm };
  if (norm.includes('razonable')) return { rank: 2, text: norm };
  return { rank: 3, text: norm };
}

/**
 * Multi-criteria comparator for health directory allies:
 * 1. Estado (A-Z)
 * 2. Localidad (A-Z)
 * 3. With indexOrder (>0) before Without indexOrder
 * 4. With indexOrder: indexOrder asc -> Negotiation rank -> Other negotiation text (A-Z) -> Nombre (A-Z)
 * 5. Without indexOrder: Negotiation rank -> Other negotiation text (A-Z) -> Nombre (A-Z)
 */
function compareAllies(a, b) {
  const estadoA = normalizeString(a.estado);
  const estadoB = normalizeString(b.estado);
  if (estadoA !== estadoB) return estadoA.localeCompare(estadoB);

  const localidadA = normalizeString(a.localidad);
  const localidadB = normalizeString(b.localidad);
  if (localidadA !== localidadB) return localidadA.localeCompare(localidadB);

  const idxA = (a.indexOrder !== null && a.indexOrder !== undefined && a.indexOrder !== '' && a.indexOrder !== 0) ? Number(a.indexOrder) : null;
  const idxB = (b.indexOrder !== null && b.indexOrder !== undefined && b.indexOrder !== '' && b.indexOrder !== 0) ? Number(b.indexOrder) : null;
  const hasIdxA = idxA !== null && !isNaN(idxA) && idxA > 0;
  const hasIdxB = idxB !== null && !isNaN(idxB) && idxB > 0;

  const negA = getNegotiationRank(a.tipo_negociacion);
  const negB = getNegotiationRank(b.tipo_negociacion);

  if (hasIdxA && hasIdxB) {
    if (idxA !== idxB) return idxA - idxB;
    if (negA.rank !== negB.rank) return negA.rank - negB.rank;
    if (negA.rank === 3 && negA.text !== negB.text) return negA.text.localeCompare(negB.text);
    return normalizeString(a.nombre).localeCompare(normalizeString(b.nombre));
  }

  if (hasIdxA) return -1;
  if (hasIdxB) return 1;

  if (negA.rank !== negB.rank) return negA.rank - negB.rank;
  if (negA.rank === 3 && negA.text !== negB.text) return negA.text.localeCompare(negB.text);
  return normalizeString(a.nombre).localeCompare(normalizeString(b.nombre));
}

/**
 * Filters the rawData based on input and selects
 */
function applyFilters() {
  const searchStr = normalizeString(state.currentSearchPhrase);

  state.filteredData = state.rawData.filter(item => {
    if (state.currentFilterEstado && normalizeString(item.estado) !== normalizeString(state.currentFilterEstado)) return false;
    if (state.currentFilterLocalidad && normalizeString(item.localidad) !== normalizeString(state.currentFilterLocalidad)) return false;
    const customField = state.categoryConfig.customFilterValue;
    if (state.currentFilterEspecialidad && customField && item[customField] !== state.currentFilterEspecialidad) return false;
    if (state.currentFilterSubEspecialidad && item.especialidad !== state.currentFilterSubEspecialidad) return false;

    if (searchStr !== '') {
      const matchFound = Object.values(item).some(val => {
        return normalizeString(val).includes(searchStr);
      });
      if (!matchFound) return false;
    }

    return true;
  });

  renderActiveFilterChips();
  renderCurrentPage();
}

/**
 * Renders active filter chips (Estado / Localidad selection indicators)
 */
function renderActiveFilterChips() {
  if (!domNodes.activeFiltersWrapper) return;

  const chipsHTML = [];

  const closeIconSvg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100" width="16" height="16" style="vertical-align:middle;flex-shrink:0;"><circle cx="50" cy="50" r="50" fill="#3BBCBF"/><path d="M33 33 L67 67 M67 33 L33 67" stroke="white" stroke-width="12" stroke-linecap="round" fill="none"/></svg>`;
  if (state.currentFilterEstado) {
    chipsHTML.push(`<button type="button" class="hd-filter-chip" data-clear="estado">
      ${state.currentFilterEstado}
      <span class="hd-chip-remove" aria-label="Quitar filtro">${closeIconSvg}</span>
    </button>`);
  }

  if (state.currentFilterLocalidad) {
    chipsHTML.push(`<button type="button" class="hd-filter-chip" data-clear="localidad">
      ${state.currentFilterLocalidad}
      <span class="hd-chip-remove" aria-label="Quitar filtro">${closeIconSvg}</span>
    </button>`);
  }

  if (state.currentFilterEspecialidad) {
    chipsHTML.push(`<button type="button" class="hd-filter-chip" data-clear="especialidad">
      ${state.currentFilterEspecialidad}
      <span class="hd-chip-remove" aria-label="Quitar filtro">${closeIconSvg}</span>
    </button>`);
  }

  if (state.currentFilterSubEspecialidad) {
    chipsHTML.push(`<button type="button" class="hd-filter-chip" data-clear="subespecialidad">
      ${state.currentFilterSubEspecialidad}
      <span class="hd-chip-remove" aria-label="Quitar filtro">${closeIconSvg}</span>
    </button>`);
  }

  domNodes.activeFiltersWrapper.innerHTML = chipsHTML.join('');
}

/**
 * Resets all active filters and reloads the full dataset
 */
function clearAllFilters() {
  state.currentFilterEstado = '';
  state.currentFilterLocalidad = '';
  state.currentFilterEspecialidad = '';
  state.currentFilterSubEspecialidad = '';
  state.currentSearchPhrase = '';
  state.currentPage = 1;

  if (domNodes.searchInput) domNodes.searchInput.value = '';
  if (domNodes.filterEstado) domNodes.filterEstado.value = '';
  if (domNodes.filterLocalidad) domNodes.filterLocalidad.value = '';
  if (domNodes.filterEspecialidad) domNodes.filterEspecialidad.value = '';
  if (domNodes.filterSubEspecialidad) domNodes.filterSubEspecialidad.value = '';

  checkSubEspecialidadVisibility();

  if (domNodes.activeFiltersWrapper) domNodes.activeFiltersWrapper.innerHTML = '';
  populateSelectFilters();
  applyFilters();
}

/**
 * Calculates pagination arrays and renders table + controls
 */
function renderCurrentPage() {
  const totalItems = state.filteredData.length;

  if (totalItems === 0) {
    domNodes.resultsContainer.innerHTML = `<div class="hd-empty-msg">No se encontraron resultados.</div>`;
    domNodes.paginationControls.innerHTML = '';
    return;
  }

  // Calculate slice
  const startIndex = (state.currentPage - 1) * state.rowsPerPage;
  let endIndex = startIndex + state.rowsPerPage;
  if (endIndex > totalItems) endIndex = totalItems;

  const currentSlice = state.filteredData.slice(startIndex, endIndex);

  // Render Table
  renderTableHTML(currentSlice);

  // Render Pagination Text and Controls
  renderPaginationControls(startIndex, endIndex, totalItems);
}

/**
 * Generates the physical DOM for the data table grouping by Localidad
 */
function renderTableHTML(dataSlice) {
  if (!dataSlice || dataSlice.length === 0) return;

  // Group by locality
  const groupedData = dataSlice.reduce((acc, item) => {
    const loc = item.localidad || 'General';
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(item);
    return acc;
  }, {});

  const activeCols = getActiveColumns();

  // Dynamic grid template for desktop
  const gridTemplate = activeCols.map(col => {
    const val = (col.value || '').toLowerCase();
    if (val === 'nombre') return '2fr';
    if (val === 'direccion') return '3fr';
    if (val.includes('telefono')) return '1.5fr';
    return '1fr';
  }).join(' ');

  let htmlResult = `<div class="hd-table-wrapper">`;

  Object.keys(groupedData).forEach(localidad => {
    // Obtener el estado del primer registro del grupo
    const primerItem = groupedData[localidad] ? groupedData[localidad][0] : null;
    const estado = primerItem ? primerItem.estado : '';

    // Separator Header
    htmlResult += `<div class="hd-table-group-header">Estado ${estado} > ${localidad}</div>`;

    // Table Header per group (Desktop)
    htmlResult += `<div class="hd-table-header hd-hide-on-mobile" style="grid-template-columns: ${gridTemplate};">`;
    activeCols.forEach(col => {
      htmlResult += `<div class="hd-th hd-center hd-font-12">${col.label}</div>`;
    });
    htmlResult += `</div>`;

    // Rows
    groupedData[localidad].forEach(item => {
      const isSubRed = item.sub_red !== null && item.sub_red !== undefined && item.sub_red !== '';
      const subRedClass = isSubRed ? 'hd-row-sub-red' : '';
      htmlResult += `<div class="hd-table-row ${subRedClass}" style="grid-template-columns: ${gridTemplate};">`;

      // Main visible part in mobile (First 2 columns always visible)
      htmlResult += `<div class="hd-row-main">`;
      activeCols.slice(0, 2).forEach(col => {
        htmlResult += `
          <div class="hd-td hd-col-${col.value}">
            <span class="hd-mobile-label">${col.label}</span>
            ${renderCellHTML(item, col)}
          </div>
        `;
      });
      htmlResult += `</div>`;

      // Collapsible body in mobile
      const hasMoreCols = activeCols.length > 2;
      if (hasMoreCols) {
        htmlResult += `<div class="hd-row-body">`;
        activeCols.slice(2).forEach((col, idx) => {
          if (idx > 0 && idx % 2 === 0) {
            htmlResult += `<hr class="hd-body-divider">`;
          }
          const isCentered = ['emergencia_dia', 'emergencia_noche', 'deposito', 'gastos_reembolsables', 'gastos_razonables'].includes(col.value);
          htmlResult += `
            <div class="hd-td hd-col-${col.value} ${isCentered ? 'hd-center' : ''}">
              <span class="hd-mobile-label">${col.label}</span>
              ${renderCellHTML(item, col)}
            </div>
          `;
        });

        // Toggle button at the bottom, only visible on mobile
        htmlResult += `
          <button class="hd-row-toggle hd-hide-on-desktop hd-row-toggle--bottom" aria-label="Ver menos">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="7 13 12 18 17 13"></polyline><polyline points="7 6 12 11 17 6"></polyline></svg>
            <span class="hd-toggle-label">Ver Menos</span>
          </button>
        </div>

        <!-- Toggle button at the bottom of hd-row-main, only visible on mobile when collapsed -->
        <button class="hd-row-toggle hd-hide-on-desktop hd-row-toggle--open" aria-label="Ver más">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="7 13 12 18 17 13"></polyline><polyline points="7 6 12 11 17 6"></polyline></svg>
          <span class="hd-toggle-label"></span>
        </button>
        `;
      }

      htmlResult += `</div>`; // Close hd-table-row
    });
  });

  htmlResult += `</div>`; // Close hd-table-wrapper
  domNodes.resultsContainer.innerHTML = htmlResult;
}

/**
 * Formats cell HTML depending on column type/value
 */
function renderCellHTML(item, col) {
  const key = col.value;
  if (!key) return '';

  const cleanKey = key.toLowerCase().trim();

  if (cleanKey === 'nombre') {
    const provName = toTitleCase(item.nombre);
    return `<strong>${provName}</strong>`;
  }
  if (cleanKey === 'direccion') {
    let addressLink = item.direccion || '';
    if (item.ubicacion_gps) {
      let cleanGps = item.ubicacion_gps.replace(/[^0-9.,\-]/g, '');
      if (cleanGps) {
        addressLink = `<a href="https://www.google.com/maps/@${cleanGps},14z" target="_blank" rel="noopener noreferrer" style="text-decoration:none; color:inherit;">${item.direccion || ''}</a>`;
      }
    }
    return `<span>${addressLink}</span>`;
  }
  if (cleanKey === 'telefono' || cleanKey.includes('telefono')) {
    const phoneArr = [item.telefono, item.telefono2, item.telefono3, item.telefono4].filter(Boolean);
    const phoneMerged = phoneArr.map(p => `<a href="tel:${p}" style="text-decoration:none; color:inherit;">${p}</a>`).join(' / ');
    return `<span>${phoneMerged || ''}</span>`;
  }
  // if (cleanKey === 'emergencia_dia' || cleanKey === 'emergencia_noche' || cleanKey.includes('emergencia')) {
  //   return getEmergHTML(item.emergencia_dia, item.emergencia_noche);
  // }
  if (cleanKey === 'deposito') {
    return item.deposito ? 'Sí' : '';
  }
  if (cleanKey === 'gastos_reembolsables') {
    return item.gastos_reembolsables ? '<span title="Reembolso"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="hd-icon-svg hd-icon-refund" stroke="#41b6b4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="17 1 21 5 17 9"></polyline><path d="M3 11V9a4 4 0 0 1 4-4h14"></path><polyline points="7 23 3 19 7 15"></polyline><path d="M21 13v2a4 4 0 0 1-4 4H3"></path></svg></span>' : '';
  }
  if (cleanKey === 'gastos_razonables') {
    return item.gastos_razonables ? '<span title="Gastos Razonables"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#41b6b4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="hd-icon-svg hd-icon-scale"><path d="m16 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="m2 16 3-8 3 8c-.87.65-1.92 1-3 1s-2.13-.35-3-1Z"/><path d="M7 21h10"/><path d="M12 3v18"/><path d="M3 7h2c2 0 5-1 7-2 2 1 5 2 7 2h2"/></svg></span>' : '';
  }
  if (typeof item[cleanKey] === 'boolean') {
    return item[cleanKey] ? 'Sí' : 'No';
  }
  return `<span>${item[cleanKey] || ''}</span>`;
}

/**
 * Helper to build the Emergency string
 */
// function getEmergHTML(dia, noche) {
//   let res = [];
//   if (dia) res.push('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="hd-icon-svg hd-icon-sun" stroke="#41b6b4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" title="Día"><circle cx="12" cy="12" r="5"></circle><line x1="12" y1="1" x2="12" y2="3"></line><line x1="12" y1="21" x2="12" y2="23"></line><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"></line><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"></line><line x1="1" y1="12" x2="3" y2="12"></line><line x1="21" y1="12" x2="23" y2="12"></line><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"></line><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"></line></svg>');
//   if (noche) res.push('<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" class="hd-icon-svg hd-icon-moon" stroke="#41b6b4" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" title="Noche"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path></svg>');
//   return res.length > 0 ? `<div class="hd-emerg-icons">${res.join('')}</div>` : '-';
// }

/**
 * Builds the numeric pagination links
 */
function renderPaginationControls(startIdx, endIdx, totalItems) {
  const totalPages = Math.ceil(totalItems / state.rowsPerPage);

  let html = `<span class="hd-pag-info">${startIdx + 1}-${endIdx} of ${totalItems}</span>`;
  html += `<div class="hd-pag-arrows">`;

  // Prev Button
  const isPrevDisabled = state.currentPage === 1;
  html += `<button class="hd-pag-btn" ${isPrevDisabled ? 'disabled' : ''} onclick="goToPage(${state.currentPage - 1})">&larr;</button>`;

  // Number Pages (MAX 5 for simplicity of MVP)
  let startPage = Math.max(1, state.currentPage - 2);
  let endPage = Math.min(totalPages, startPage + 4);
  if (endPage - startPage < 4) {
    startPage = Math.max(1, endPage - 4);
  }

  for (let i = startPage; i <= endPage; i++) {
    const activeClass = i === state.currentPage ? 'active' : '';
    html += `<button class="hd-pag-btn hd-pag-num ${activeClass}" onclick="goToPage(${i})">${i}</button>`;
  }

  // Next Button
  const isNextDisabled = state.currentPage === totalPages;
  html += `<button class="hd-pag-btn" ${isNextDisabled ? 'disabled' : ''} onclick="goToPage(${state.currentPage + 1})">&rarr;</button>`;

  html += `</div>`;
  domNodes.paginationControls.innerHTML = html;
}

/**
 * Global function attached to pagination onclicks
 */
window.goToPage = function (pageNumber) {
  state.currentPage = pageNumber;
  renderCurrentPage();
};

/**
 * Preloads the PDF logo and caches it as a Base64 PNG data URL
 */
function preloadPdfLogo() {
  const section = domNodes.sectionElement || document.querySelector('.health-directory-section');
  if (!section) return;
  const logoUrl = section.getAttribute('data-pdf-logo');
  if (!logoUrl) return;

  const img = new Image();
  img.crossOrigin = 'Anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth || 200;
      canvas.height = img.naturalHeight || 38;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      state.pdfLogoData = {
        dataUrl: canvas.toDataURL('image/png'),
        width: canvas.width,
        height: canvas.height
      };
    } catch (err) {
      state.pdfLogoData = {
        dataUrl: logoUrl,
        width: img.naturalWidth || 200,
        height: img.naturalHeight || 38
      };
    }
  };
  img.onerror = () => {
    console.warn('Directorio Salud: no se pudo precargar el logo del PDF desde:', logoUrl);
  };
  img.src = logoUrl;
}

/**
 * Ensures the PDF logo is loaded before rendering the PDF document
 */
async function ensurePdfLogo() {
  if (state.pdfLogoData) return state.pdfLogoData;
  const section = domNodes.sectionElement || document.querySelector('.health-directory-section');
  if (!section) return null;
  const logoUrl = section.getAttribute('data-pdf-logo');
  if (!logoUrl) return null;

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || 200;
        canvas.height = img.naturalHeight || 38;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        state.pdfLogoData = {
          dataUrl: canvas.toDataURL('image/png'),
          width: canvas.width,
          height: canvas.height
        };
        resolve(state.pdfLogoData);
      } catch (err) {
        state.pdfLogoData = {
          dataUrl: logoUrl,
          width: img.naturalWidth || 200,
          height: img.naturalHeight || 38
        };
        resolve(state.pdfLogoData);
      }
    };
    img.onerror = () => resolve(null);
    img.src = logoUrl;
  });
}

/**
 * Generates a PDF of the current category (full rawData) using jsPDF and jspdf-autotable
 */
async function generatePDF() {
  if (!window.jspdf) {
    alert("La librería PDF aún no ha cargado. Por favor intente en unos segundos.");
    return;
  }
  if (!state.filteredData || state.filteredData.length === 0) {
    alert("No hay datos filtrados para exportar.");
    return;
  }

  const logoData = await ensurePdfLogo();

  const { jsPDF } = window.jspdf;
  const doc = new jsPDF('landscape');

  const catTitle = state.currentCategoryTitle || 'Directorio';

  // 1. Título principal del documento
  doc.setFontSize(15);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(20, 33, 87); // #142157 Navy Seguros Venezuela
  doc.text(`Listado de ${catTitle} - Seguros Venezuela`, 14, 13);

  // 2. Subtítulo institucional y fecha de emisión
  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 110, 125);
  const formattedDate = new Date().toLocaleDateString('es-VE', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.text(`Red de Aliados • Documento referencial generado el ${formattedDate}`, 14, 18);

  // 3. Renderizar logo corporativo en la esquina superior derecha
  if (logoData && logoData.dataUrl) {
    try {
      const aspect = (logoData.width && logoData.height) ? (logoData.width / logoData.height) : (200 / 38);
      const logoW = 44;
      const logoH = Math.round((logoW / aspect) * 10) / 10;
      const logoX = 297 - 14 - logoW; // Margen derecho 14mm
      const logoY = 8;
      doc.addImage(logoData.dataUrl, 'PNG', logoX, logoY, logoW, logoH);
    } catch (imgErr) {
      console.warn('Directorio Salud: no se pudo incrustar el logo en el PDF:', imgErr);
    }
  }

  const activeCols = getActiveColumns();
  const tableHeaders = activeCols.map(col => col.label);

  // 4. Agrupar la data filtrada actual por localidad
  const groupedData = state.filteredData.reduce((acc, item) => {
    const loc = item.localidad || 'General';
    if (!acc[loc]) acc[loc] = [];
    acc[loc].push(item);
    return acc;
  }, {});

  const tableBody = [];

  // 5. Construir el cuerpo de la tabla intercalando las cabeceras por grupo
  Object.keys(groupedData).forEach(localidad => {
    const groupItems = groupedData[localidad];
    const primerItem = groupItems[0];
    const estado = primerItem ? primerItem.estado : '';

    // A. Fila del Separador de Grupo (Fondo gris)
    tableBody.push([
      {
        content: `Estado ${estado} > ${localidad}`,
        colSpan: activeCols.length,
        styles: {
          fillColor: [240, 242, 245],
          textColor: [44, 62, 80],
          fontStyle: 'bold',
          halign: 'left',
          fontSize: 9
        }
      }
    ]);

    // B. Fila de Encabezados de Columnas para este grupo (Fondo Navy #142157)
    const localHeadersRow = activeCols.map(col => {
      return {
        content: col.label,
        styles: {
          fillColor: [20, 33, 87], // #142157 Navy Seguros Venezuela
          textColor: 255, // Blanco
          fontStyle: 'bold',
          fontSize: 8,
          halign: 'center'
        }
      };
    });
    tableBody.push(localHeadersRow);

    // C. Añadir los aliados pertenecientes a este grupo (Fondo blanco)
    groupItems.forEach(item => {
      const isSubRed = item.sub_red !== null && item.sub_red !== undefined && item.sub_red !== '';
      const rowData = activeCols.map(col => {
        const key = col.value;
        if (!key) return '';
        const cleanKey = key.toLowerCase().trim();
        let val = '';
        if (cleanKey === 'nombre') {
          val = toTitleCase(item.nombre);
        } else if (cleanKey === 'direccion') {
          val = item.direccion || '';
        } else if (cleanKey === 'telefono' || cleanKey.includes('telefono')) {
          val = [item.telefono, item.telefono2, item.telefono3, item.telefono4].filter(Boolean).join(' / ');
        } else if (cleanKey === 'deposito') {
          val = item.deposito ? 'Sí' : 'No';
        } else if (cleanKey === 'gastos_reembolsables') {
          val = item.gastos_reembolsables ? 'Sí' : 'No';
        } else if (cleanKey === 'gastos_razonables') {
          val = item.gastos_razonables ? 'Sí' : 'No';
        } else if (typeof item[cleanKey] === 'boolean') {
          val = item[cleanKey] ? 'Sí' : 'No';
        } else {
          val = item[cleanKey] || '';
        }

        if (isSubRed) {
          return {
            content: val,
            styles: {
              textColor: [0, 75, 176], // #004bb0
              fontStyle: 'bold'
            }
          };
        }
        return val;
      });
      tableBody.push(rowData);
    });
  });

  // 6. Definir estilos de ancho específicos para las columnas en el PDF
  const columnStyles = {};
  activeCols.forEach((col, index) => {
    const cleanKey = (col.value || '').toLowerCase().trim();
    if (cleanKey === 'direccion') {
      columnStyles[index] = { cellWidth: 90 }; // Limitar el ancho de la dirección
    }
  });

  // 7. Renderizar la tabla en jsPDF (Desactivamos el head por defecto)
  doc.autoTable({
    head: [],
    body: tableBody,
    startY: 24,
    styles: {
      fontSize: 8,
      cellPadding: 3
    },
    columnStyles: columnStyles,
    alternateRowStyles: {
      fillColor: [255, 255, 255]
    },
    margin: { top: 24, left: 14, right: 14 }
  });

  // 8. Inyectar aviso/nota de categoría activa al final del PDF
  if (domNodes.categoryNotice && domNodes.categoryNotice.style.display !== 'none') {
    const noticeText = domNodes.categoryNotice.innerText.trim();
    if (noticeText !== '') {
      const finalY = doc.lastAutoTable.finalY + 12;

      doc.setFontSize(9);
      doc.setTextColor(80, 90, 100);
      doc.setFont("helvetica", "normal");

      const splitLines = doc.splitTextToSize(`Nota: ${noticeText}`, 269);
      doc.text(splitLines, 14, finalY);
    }
  }

  const pdfBlob = doc.output('bloburl');
  window.open(pdfBlob, '_blank');
}

/**
 * Scrolls the active category button into view inside the horizontal categories wrapper
 * if it's partially or fully out of view, and scrolls vertically to the directory section if cat param was provided.
 */
function scrollActiveCategoryIntoView(btn) {
  if (!btn) return;

  // 1. Desplazamiento horizontal para centrar el botón de categoría en modo carrusel
  if (domNodes.categoriesScrollWrapper) {
    const isGrid = domNodes.categoriesCarouselContainer && domNodes.categoriesCarouselContainer.classList.contains('hd-layout-grid');
    if (!isGrid) {
      const wrapper = domNodes.categoriesScrollWrapper;
      const btnLeft = btn.offsetLeft;
      const btnWidth = btn.offsetWidth;
      const wrapperScrollLeft = wrapper.scrollLeft;
      const wrapperWidth = wrapper.clientWidth;

      const isLeftHidden = btnLeft < wrapperScrollLeft;
      const isRightHidden = (btnLeft + btnWidth) > (wrapperScrollLeft + wrapperWidth);

      if (isLeftHidden || isRightHidden) {
        const targetScrollLeft = btnLeft - (wrapperWidth / 2) + (btnWidth / 2);
        wrapper.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth'
        });
      }
    }
  }

  // 2. Desplazamiento vertical suave a la sección del directorio si vino por parámetro URL
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.has('cat') && domNodes.sectionElement) {
    setTimeout(() => {
      const headerOffset = 80;
      const elementPosition = domNodes.sectionElement.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }, 200);
  }
}

